import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { forkJoin } from 'rxjs';
import {
  DollarSign,
  LucideAngularModule,
  PackageMinus,
  ShoppingCart,
  TrendingUp,
  Ban,
} from 'lucide-angular';
import { ChartModule } from 'primeng/chart';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { DatePickerModule } from 'primeng/datepicker';
import { ButtonModule } from 'primeng/button';

import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../core/services/auth.service';
import { InventoryService } from '../../core/services/inventory.service';
import { StoreService } from '../../core/services/store.service';
import { CurrencyService } from '../../core/services/currency.service';
import { LanguageService } from '../../core/services/language.service';
import {
  CategoryAnalyticsItem,
  HourlySalesPoint,
  InventoryAnalyticsDto,
  RevenueTrendItem,
} from '../../core/models/product.models';
import { StoreLiteDto } from '../../core/models/store.models';
import { storePicker } from '../../core/utils/store-picker';
import { MoneyFixedPipe, MoneyValuePipe } from '../../shared/pipes/money.pipe';

// Color palette for category chart (cycles if more than 8 categories)
const CAT_PALETTE = [
  '#22c55e',
  '#16a34a',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#06b6d4',
  '#f97316',
  '#84cc16',
];

@Component({
  selector: 'app-analytics',
  standalone: true,
  imports: [
    MoneyValuePipe,
    CommonModule,
    FormsModule,
    LucideAngularModule,
    ChartModule,
    SelectModule,
    MultiSelectModule,
    DatePickerModule,
    ButtonModule,
    TranslatePipe,
    MoneyFixedPipe,
  ],
  templateUrl: './analytics.component.html',
})
export class AnalyticsComponent implements OnInit {
  private inventorySvc = inject(InventoryService);
  private storeSvc = inject(StoreService);
  readonly currencySvc = inject(CurrencyService);
  private authSvc = inject(AuthService);
  private route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  private readonly langSvc = inject(LanguageService);

  // Icons
  DollarSign = DollarSign;
  TrendingUp = TrendingUp;
  ShoppingCart = ShoppingCart;
  PackageMinus = PackageMinus;
  Ban = Ban;

  // Lookup
  stores = signal<StoreLiteDto[]>([]);

  /** Bitta do'konli biznesda filtr yashiriladi — `storePicker` ga qarang. */
  readonly storePick = storePicker(this.stores);

  // Filters
  filterStoreIds: string[] = [];
  filterFrom: Date | null = (() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  })();
  filterTo: Date | null = new Date();

  // State
  analytics = signal<InventoryAnalyticsDto | null>(null);
  revenueTrend = signal<RevenueTrendItem[]>([]);
  categoryStats = signal<CategoryAnalyticsItem[]>([]);
  hourlySales = signal<HourlySalesPoint[]>([]);
  loading = signal(false);

  /**
   * Qaysi valyuta daftari ko'rsatilyapti. Bo'sh — backend kotirovka valyutasini
   * oladi. To'rt so'rov ham SHU valyutada yuboriladi (I1).
   */
  selectedCurrency = signal('');

  /**
   * Yorliqlar uchun haqiqiy valyuta. Tanlanmagan bo'lsa kotirovka valyutasi —
   * backend ham aynan shuni oladi, shuning uchun yorliq har doim to'g'ri.
   */
  currency = computed(() => this.selectedCurrency() || this.currencySvc.defaultCode());

  // ── KPI cards ────────────────────────────────────────────────────────────────
  // `langSvc.currentLang()` shu yerda ATAYLAB o'qiladi — computed'ning boshqa
  // hech qanday bog'liqligi til bilan aloqador emas, shuning uchun usiz til
  // almashganda kartalar eskicha (avvalgi tildagi) matnda qolib ketardi.
  kpiCards = computed(() => {
    const a = this.analytics();
    this.langSvc.currentLang();
    return [
      {
        name: this.translate.instant('ANALYTICS.KPI_CARDS.REVENUE'),
        value: a?.totalRevenue ?? 0,
        icon: DollarSign,
        color: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
        money: true,
      },
      {
        name: this.translate.instant('ANALYTICS.KPI_CARDS.PROFIT'),
        value: a?.grossProfit ?? 0,
        icon: TrendingUp,
        color: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400',
        money: true,
      },
      {
        name: this.translate.instant('ANALYTICS.KPI_CARDS.SALES_COUNT'),
        value: a?.totalSalesCount ?? 0,
        icon: ShoppingCart,
        color: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
        money: false,
      },
      {
        name: this.translate.instant('ANALYTICS.KPI_CARDS.WRITEOFF_LOSS'),
        value: a?.writeOffLoss ?? 0,
        icon: PackageMinus,
        color: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
        money: true,
      },
      {
        name: this.translate.instant('ANALYTICS.KPI_CARDS.WITHHELD'),
        value: a?.totalWithheld ?? 0,
        icon: Ban,
        color: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
        money: true,
      },
    ];
  });

  // ── Donut: revenue breakdown (COGS vs Gross Profit) ─────────────────────────
  donutData = computed(() => {
    const a = this.analytics();
    this.langSvc.currentLang();
    return {
      labels: [
        this.translate.instant('ANALYTICS.CHART_LABELS.COGS'),
        this.translate.instant('ANALYTICS.KPI_CARDS.PROFIT'),
      ],
      datasets: [
        {
          data: [a?.totalCOGS ?? 0, a?.grossProfit ?? 0],
          backgroundColor: ['#f59e0b', '#10b981'],
          borderWidth: 0,
          hoverOffset: 6,
        },
      ],
    };
  });

  readonly donutOptions = {
    responsive: true,
    maintainAspectRatio: true,
    cutout: '68%',
    plugins: { legend: { display: false } },
  };

  // ── Bar: financial summary ───────────────────────────────────────────────────
  financialBarData = computed(() => {
    const a = this.analytics();
    this.langSvc.currentLang();
    return {
      labels: [
        this.translate.instant('ANALYTICS.CHART_LABELS.REVENUE_SHORT'),
        this.translate.instant('ANALYTICS.CHART_LABELS.COST_SHORT'),
        this.translate.instant('ANALYTICS.KPI_CARDS.PROFIT'),
        this.translate.instant('ANALYTICS.KPI_CARDS.WRITEOFF_LOSS'),
      ],
      datasets: [
        {
          label: this.currency(),
          data: [
            a?.totalRevenue ?? 0,
            a?.totalCOGS ?? 0,
            a?.grossProfit ?? 0,
            a?.writeOffLoss ?? 0,
          ],
          backgroundColor: ['#3b82f6', '#f59e0b', '#10b981', '#ef4444'],
          borderRadius: 8,
          borderSkipped: false,
        },
      ],
    };
  });

  readonly financialBarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 1.2,
    plugins: { legend: { display: false } },
    scales: {
      x: { grid: { display: false } },
      y: { beginAtZero: true },
    },
  };

  // ── Line: revenue trend (DYNAMIC) ────────────────────────────────────────────
  revenueTrendData = computed(() => {
    const items = this.revenueTrend();
    this.langSvc.currentLang();
    return {
      labels: items.map((i) => i.monthName),
      datasets: [
        {
          data: items.map((i) => i.revenue),
          label: this.translate.instant('ANALYTICS.CHART_LABELS.REVENUE_SHORT'),
          borderColor: '#3b82f6',
          backgroundColor: 'rgba(59,130,246,0.15)',
          fill: true,
          tension: 0.35,
          pointBackgroundColor: '#3b82f6',
          pointBorderColor: '#3b82f6',
          pointRadius: 4,
        },
      ],
    };
  });

  readonly revenueTrendOptions = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 1.6,
    plugins: { legend: { display: false } },
    scales: {
      x: { grid: { display: false } },
      y: { beginAtZero: true },
    },
  };

  // ── Bar: category breakdown (DYNAMIC) ────────────────────────────────────────
  categoryBarData = computed(() => {
    const items = this.categoryStats();
    return {
      labels: items.map((i) => i.categoryName),
      datasets: [
        {
          label: this.currency(),
          data: items.map((i) => i.revenue),
          backgroundColor: items.map((_, idx) => CAT_PALETTE[idx % CAT_PALETTE.length]),
          borderRadius: 6,
          borderSkipped: false,
        },
      ],
    };
  });

  readonly categoryBarOptions = {
    responsive: false,
    maintainAspectRatio: false,
    aspectRatio: 1.5,
    plugins: { legend: { display: false } },
    scales: {
      x: { grid: { display: false } },
      y: { beginAtZero: true },
    },
  };

  // ── Bar: orders by hour (DYNAMIC) ────────────────────────────────────────────
  ordersBarData = computed(() => {
    const items = this.hourlySales();
    this.langSvc.currentLang();
    return {
      labels: items.map((i) => `${String(i.hour).padStart(2, '0')}:00`),
      datasets: [
        {
          data: items.map((i) => i.orders),
          label: this.translate.instant('ANALYTICS.CHART_LABELS.ORDERS'),
          backgroundColor: '#a855f7',
          borderRadius: 8,
        },
      ],
    };
  });

  readonly ordersBarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 1.6,
    plugins: { legend: { display: false } },
    scales: {
      x: { grid: { display: false } },
      y: { beginAtZero: true },
    },
  };

  ngOnInit() {
    this.storeSvc
      .getAllLite()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const storeList = res.data ?? [];
          this.stores.set(storeList);
          // Do'konlar sahifasidan kelgan bo'lsa — ayni shu do'kon tanlanadi
          const storeId = this.route.snapshot.queryParamMap.get('storeId');
          if (storeId && storeList.some((s) => s.id === storeId)) {
            this.filterStoreIds = [storeId];
          } else if (this.authSvc.user()?.role === 'Employee' && storeList.length > 0) {
            this.filterStoreIds = [storeList[0].id];
          }
          this.loadAnalytics();
        },
      });
  }

  /** Valyuta almashganda darhol qayta yuklanadi — "Qo'llash" bosish shart emas. */
  onCurrencyChange(code: string) {
    this.selectedCurrency.set(code);
    this.loadAnalytics();
  }

  loadAnalytics() {
    this.loading.set(true);
    const storeIds = this.filterStoreIds.length ? this.filterStoreIds : undefined;
    const fromDate = this.filterFrom ? this.filterFrom.toISOString() : undefined;
    const toAdjusted = this.filterTo ? new Date(this.filterTo) : null;
    if (toAdjusted) toAdjusted.setHours(23, 59, 59, 999);
    const toDate = toAdjusted ? toAdjusted.toISOString() : undefined;

    // Hamma so'rov BIR XIL valyutada bo'lishi shart — aks holda donut, trend va
    // KPI kartalari boshqa-boshqa daftardan chiqib, foiz ham noto'g'ri bo'lardi.
    const currency = this.selectedCurrency() || undefined;

    forkJoin({
      analytics: this.inventorySvc.getAnalytics(storeIds, fromDate, toDate, currency),
      trend: this.inventorySvc.getRevenueTrend(storeIds, fromDate, toDate, currency),
      categories: this.inventorySvc.getCategoryAnalytics(storeIds, fromDate, toDate, currency),
      hourly: this.inventorySvc.getHourlySales(storeIds, fromDate, toDate, currency),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ analytics, trend, categories, hourly }) => {
          this.analytics.set(analytics.data ?? null);
          this.revenueTrend.set(trend.data ?? []);
          this.categoryStats.set(categories.data ?? []);
          this.hourlySales.set(hourly.data ?? []);
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }
}
